/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { AelEvents } from '@gh/builder-shared-data';
import {
  ActionFlow,
  getDefaultEmail,
  getRouteInitData,
  resetPrivilegedTokensAndUpdate,
  TextDictionary,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DEFAULT_EMAIL_SUFFIX } from '@gh/config';
import {
  AddressAutocompleteComponent,
  AddressComponents,
  DialogService,
  getControlValue,
  NotificationService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';

import { assign, isEmpty } from 'lodash';
import { SystemVarsFranchiseeBusinessService } from '../../resources';
import { SystemVarsFranchisee } from '../../resources/model/system-vars.model';
import { SystemVarsFranchiseeFormState } from './system-vars-franchisee-form.service';

@Component({
  standalone: false,
  selector: 'gh-system-vars-franchisee-tab',
  templateUrl: 'system-vars-franchisee-tab.component.html',
  styleUrls: ['system-vars-franchisee-tab.component.scss'],
  viewProviders: [
    SystemVarsFranchiseeFormState,
  ],
})
export class SystemVarsFranchiseeTabComponent extends AbstractFormComponent<SystemVarsFranchisee> implements OnInit, AfterViewInit {
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent | undefined;

  emailSuffix = DEFAULT_EMAIL_SUFFIX;
  states: TextDictionary;
  showManualAddressFields = false;
  private original: SystemVarsFranchisee;
  private _currentTab: string = 'common';

  constructor(public form: SystemVarsFranchiseeFormState,
    private route: ActivatedRoute,
    private systemVarsBusiness: SystemVarsFranchiseeBusinessService,
    private userContext: BuilderUserContext,
    private navigationService: BuilderNavigationService,
    private dialogService: DialogService,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    private notificationService: NotificationService,
    private actions: CommonActions) {
    super();
  }

  get email(): string {
    return getDefaultEmail(getControlValue(this.form.group, 'email'));
  }

  get completeFlow(): ActionFlow<FormFlowState<SystemVarsFranchisee>> {
    let invalidFormPath = '';
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        (value) => {
          if (value.trigger === 'process') {
            if (this.form.group.controls['billing'].invalid) {
              invalidFormPath = 'billing';
            } else {
              if (this.form.group.invalid) {
                invalidFormPath = 'common';
              } else {
                invalidFormPath = '';
              }
            }
            if (!isEmpty(invalidFormPath)) {
              this.onTabChanged(invalidFormPath);
              if (this.form.group.controls[invalidFormPath]) {
                this.form.group.controls[invalidFormPath].markAllAsTouched();
              }
            }
          }
          return !isEmpty(invalidFormPath);
        },
        () => this.notificationService.error('msg_he_incorrect_data_in_tables')),
      execute: this.actions.execute((state: FormFlowState<SystemVarsFranchisee>) =>
        this.systemVarsBusiness.update(state.form.submitValue)),
      finish: (state: FormFlowState<SystemVarsFranchisee>) => {
        const { value } = state;
        this.original = value;
        this.form.build(value);
      },
    });
  }

  get currentTab(): string {
    return this._currentTab;
  }

  minDate(type: string): Date {
    return this.form.get(type)?.value;
  }

  minRenewalDate(): Date {
    const currentAgreement = this.form.get('billing.currentAgreement')?.value;
    const originalCommencement = this.form.get('billing.originalCommencement')?.value;
    if (currentAgreement && originalCommencement) {
      return currentAgreement.getTime() > originalCommencement.getTime() ? currentAgreement : originalCommencement;
    } else {
      if (!currentAgreement && originalCommencement) {
        return originalCommencement;
      } else {
        if (!originalCommencement && currentAgreement) {
          return currentAgreement;
        } else {
          return new Date(1970);
        }
      }
    }
  }

  ngOnInit(): void {
    const { states, franchiseeSystemVariables: original } = getRouteInitData(this.route);
    assign(this, { states, original });
    this.form.build(original);

    const hasAddressData = !!(this.form.group.get('street1')?.value ||
      this.form.group.get('street2')?.value ||
      this.form.group.get('city')?.value ||
      this.form.get('state')?.value ||
      this.form.group.get('postcode')?.value);
    this.showManualAddressFields = hasAddressData;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.onAddressChange();
    });
  }

  onReset(): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_reset')).pipe(
        filter$(yesAnswer))
      .subscribe(() => {
        resetPrivilegedTokensAndUpdate(this.form);
        this.form.build(this.original);
      });
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'OFFICE',
      eventTypeIds: [AelEvents.Office.EditedFranchisee],
      label: label ? label : 'lbl_user_franchisee',
    });
  }

  onApplyLicense(): void {
    this.form.updateLicenseState();
  }

  onTabChanged(name: string): void {
    if (this._currentTab !== name) {
      this._currentTab = name;
    }
  }

  onAssignmentFeeUpdate(): void {
    this.form.onRecalculateAssignmentFee();
  }

  onRenewalDateUpdate(): void {
    this.form.onRecalculateRenewalDate();
  }

  onAddressChange() {
    const street1 = this.form.group.get('street1')?.value || '';
    const street2 = this.form.group.get('street2')?.value || '';
    const city = this.form.group.get('city')?.value || '';
    const state = this.form.group.get('state')?.value || '';
    const postCode = this.form.group.get('postCode')?.value || '';

    const addressParts: string[] = [];

    const streetCombined = [street2, street1].filter(Boolean).join(' ');
    if (streetCombined) addressParts.push(streetCombined);
    if (city) addressParts.push(city);
    if (state) addressParts.push(state);
    if (postCode) addressParts.push(postCode);

    const newAddress = addressParts.join(', ');

    if (this.addressAutocomplete) {
      this.addressAutocomplete.inputValue = newAddress;
    }
  }

  onAddressSelected(components: AddressComponents): void {
    const streetParts: string[] = [];
    if (components.streetNo) streetParts.push(components.streetNo);
    if (components.street) streetParts.push(components.street);
    const street1Value = streetParts.join(' ').trim();

    this.form.group.patchValue({
      street1: street1Value || '',
      city: components.city || '',
      state: components.state || '',
      postCode: components.postalCode || '',
    });

    ['street1', 'city', 'state', 'postCode'].forEach(field => {
      const control = this.form.group.get(field);
      if (control) control.markAsDirty();
    });
  }
}
